import { Fields } from '../generator/types';
import { formatDate, formatRidersText } from '../generator/utils';

import { readFieldValue } from './fieldKeys';

export interface ResolvedField {
  // The resolved display string. For a reserved field this is the live value
  // (riders, carrierDisplayName). For a user-input field this is the typed
  // value, or empty string when nothing was typed.
  value: string;
  // Whether `value` is a real user/case-derived value vs. a fallback to the
  // placeholder hint. Renderers use this to style empty fields differently.
  isFilled: boolean;
  // Stable placeholder text to show when no value is present.
  placeholder: string;
}

export interface ResolveFieldInput {
  key: string;
  fieldValues: Record<string, string>;
  fieldPlaceholders: Record<string, string>;
  riders: string[];
  carrierDisplayName: string;
  // Block the token belongs to; selects that block's own typed value.
  blockId?: string;
}

// Single source of truth for resolving a {{field}} token to a display string.
// Used by both the HTML in-drawer renderer and the react-pdf renderer so they
// can never disagree on date formatting, reserved field handling, etc.
export function resolveFieldToken(input: ResolveFieldInput): ResolvedField {
  const {
    key,
    fieldValues,
    fieldPlaceholders,
    riders,
    carrierDisplayName,
    blockId,
  } = input;
  const placeholder = fieldPlaceholders[key] ?? key;

  if (key === 'riders') {
    return { value: formatRidersText(riders), isFilled: true, placeholder };
  }
  if (key === 'carrierDisplayName') {
    return { value: carrierDisplayName, isFilled: true, placeholder };
  }

  const raw = readFieldValue(fieldValues, key, blockId);
  if (raw === undefined || raw === '') {
    return { value: '', isFilled: false, placeholder };
  }

  if (key === Fields.dateMeasurementTaken) {
    try {
      return { value: formatDate(raw), isFilled: true, placeholder };
    } catch {
      return { value: raw, isFilled: true, placeholder };
    }
  }
  return { value: raw, isFilled: true, placeholder };
}
